import { Helmet } from "react-helmet";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import "../Modules/faq/faq.css";
import Footer from "../Modules/Footer";
import NavigationBar from "../Modules/NavigationBar";
import FaqView from "../Modules/faq/FaqView";
import ProductPicker from "../Modules/faq/ProductPicker";
import { products } from "../Modules/faq/content/products";
import { Language, ProductId } from "../Modules/faq/types";

/** Earlier links used these ids; keep them working. */
const LEGACY_IDS: Record<string, ProductId> = { "protect-v1": "PRO-TW1-ORG", "protect-v2": "PRO-TW2-ORG", myearsonly: "PRO-TW1-ORG", myprotect: "PRO-TW2-ORG" };

const UI = {
	EN: {
		support: "Support",
		pick: "Select your product",
		back: "All products",
		articleCode: "Article code",
		downloads: "Downloads",
		notes: { note: "Note", tip: "Tip", warning: "Warning", important: "Important" },
	},
	NL: {
		support: "Support",
		pick: "Kies je product",
		back: "Alle producten",
		articleCode: "Artikelcode",
		downloads: "Downloads",
		notes: { note: "Let op", tip: "Tip", warning: "Waarschuwing", important: "Belangrijk" },
	},
} as const;

export default function FAQ() {
	const [params, setParams] = useSearchParams();

	const lang: Language = params.get("lang")?.toUpperCase() === "NL" ? "NL" : "EN";
	const requested = (params.get("product") ?? "").toLowerCase();
	const product = products.find((p) => p.id.toLowerCase() === (LEGACY_IDS[requested]?.toLowerCase() ?? requested));
	const t = UI[lang];

	function select(id: ProductId | null, language: Language = lang) {
		const next: Record<string, string> = {};
		if (id) next.product = id;
		if (language === "NL") next.lang = "nl";
		setParams(next);
		window.scrollTo({ top: 0 });
	}

	const faq = product?.faq[lang];

	return (
		<div className="eo-root">
			<Helmet>
				<title>{`EarsOnly | FAQ${product ? ` - Protect ${product.id}` : ""}`}</title>
			</Helmet>
			<NavigationBar />
			<Container className="faq-page">
				<div className="faq-toolbar">
					{product ? (
						<button type="button" className="faq-back" onClick={() => select(null)}>
							<span aria-hidden="true">←</span> {t.back}
						</button>
					) : (
						<span />
					)}
					<div className="faq-lang" role="group" aria-label="Language">
						{(["EN", "NL"] as Language[]).map((l) => (
							<button
								key={l}
								type="button"
								aria-pressed={lang === l}
								onClick={() => select(product?.id ?? null, l)}
							>
								{l}
							</button>
						))}
					</div>
				</div>

				{!product && (
					<>
						<p className="faq-overline">{t.support}</p>
						<h1 className="faq-title">{t.pick}</h1>
						<ProductPicker products={products} lang={lang} onSelect={(id) => select(id)} />
					</>
				)}

				{product && faq && (
					<>
						<p className="faq-overline">{t.support}</p>
						<h1 className="faq-title">
							EarsOnly {product.name.split(" (")[0]}
							<span className="faq-title__code">{t.articleCode}: {product.id}</span>
							<span className="faq-title__sub">{faq.title}</span>
						</h1>
						<FaqView key={`${product.id}-${lang}`} faq={faq} lang={lang} downloadsTitle={t.downloads} noteLabels={t.notes} />
					</>
				)}
			</Container>
			<Footer />
		</div>
	);
}
